import { FormField } from "./formFieldTypes";


export interface AddressComponents {
  streetNumber?: string;
  streetName?: string;
  suburb?: string;
  city?: string;
  state?: string;
  postcode?: string;
  countryCode?: string;
  streetType?: string;
  unitNumber?: string;
  formattedAddress?: string;
}

export interface ExtractedAddress {
  residential: AddressComponents;
  postal: AddressComponents;
}

/**
 * Convert Google Places address component type to our format
 * Maps Google's types to our field system_identifiers
 */
const getAddressComponentValue = (
  components: google.maps.GeocoderAddressComponent[],
  types: string[],
  useShortName = false
) => {
  const component = components.find(c =>
    types.some(type => c.types.includes(type))
  );

  return component
    ? useShortName
      ? component.short_name
      : component.long_name
    : '';
};

/**
 * Extract address components from Google Places result
 */
export const extractAddressComponents = (
  placeResult: google.maps.places.PlaceResult
): AddressComponents => {
  if (!placeResult.address_components) {
    return {};
  }

  const components = placeResult.address_components;
  const formattedAddress = placeResult.formatted_address || 'N/A';

  // Extract individual components
  const streetNumber = getAddressComponentValue(components, ['street_number']);
  const streetName = getAddressComponentValue(components, ['route']);
  const suburb = getAddressComponentValue(components, [
    'locality',
    'postal_town',
    'sublocality_level_1',
  ]);
  const city = getAddressComponentValue(components, [
    'administrative_area_level_2',
  ]);
  const state = getAddressComponentValue(components, ['administrative_area_level_1'], true);
  const postcode = getAddressComponentValue(components, ['postal_code']);
  const countryCode = getAddressComponentValue(components, ['country'], true);

  // Try to extract country code from ISO-3166-1 alpha-3 format
  // let countryCode = 'AUS'; // Default to Australia

  // if (country) {
  //   // Map common country codes
  //   const countryCodeMap: Record<string, string> = {
  //     AU: 'AUS',
  //     NZ: 'NZL',
  //     GB: 'GBR',
  //     US: 'USA',
  //   };
  //   countryCode = countryCodeMap[country] || country;
  // }

  return {
    streetNumber: streetNumber?.trim(),
    streetName: streetName?.trim(),
    suburb: suburb?.trim(),
    city: city?.trim(),
    state: state?.trim(),
    postcode: postcode?.trim(),
    countryCode,
    streetType: undefined, // Google doesn't provide street type separately
    unitNumber: undefined, // Google doesn't provide unit number
    formattedAddress, // Include formatted address for reference
  };
};

export const autoCompleteAddressFields = (
  placeResult: google.maps.places.PlaceResult,
  currentStepData: FormField[],
  selectedValues: Record<number, any>,
  addressType: 'residential' | 'postal'
): Record<number, any> => {
  const addressComponents = extractAddressComponents(placeResult);
  const updatedValues = { ...selectedValues };

  // Mapping of address components to form field system_identifiers based on address type
  const fieldMappings: Record<string, Record<string, string>> = {
    residential: {
      streetNumber: 'residentialAddress.streetNumber',
      streetName: 'residentialAddress.streetName',
      streetType: 'residentialAddress.streetType',
      unitNumber: 'residentialAddress.unit',
      suburb: 'residentialAddress.suburb',
      city: 'residentialAddress.city',
      state: 'residentialAddress.state',
      postcode: 'residentialAddress.postcode',
      countryCode: 'residentialAddress.countryCode',
      formattedAddress: 'searchAddress',
    },
    postal: {
      streetNumber: 'postalAddress.streetNumber',
      streetName: 'postalAddress.streetName',
      streetType: 'postalAddress.streetType',
      unitNumber: 'postalAddress.unit',
      suburb: 'postalAddress.suburb',
      city: 'postalAddress.city',
      state: 'postalAddress.state',
      postcode: 'postalAddress.postcode',
      countryCode: 'postalAddress.countryCode',
      formattedAddress: 'postalAddress.searchAddress',
    },
  };

  const mappings = fieldMappings[addressType];

  // Find relevant fields and update values
  currentStepData.forEach((field) => {
    const systemId = field.system_identifire;

    // Check if this field matches any of our address mappings
    Object.entries(mappings).forEach(([componentKey, mappedSystemId]) => {
      if (systemId === mappedSystemId && addressComponents[componentKey as keyof AddressComponents]) {
        const fieldId = field.id;
        updatedValues[fieldId] = addressComponents[componentKey as keyof AddressComponents];
      }
    });
  });

  return updatedValues;
};

export const autoCompleteAllAddressFields = (
  placeResult: google.maps.places.PlaceResult,
  currentStepData: FormField[],
  selectedValues: Record<number, any>,
  postalSameAsResidential: boolean = true,
  triggerSystemIdentifier: string = 'searchAddress'
): Record<number, any> => {
  const isPostalSearch = triggerSystemIdentifier === 'postalAddress.searchAddress';

  if (isPostalSearch) {
    // Only fill postal address fields
    return autoCompleteAddressFields(
      placeResult,
      currentStepData,
      selectedValues,
      'postal'
    );
  }

  // Residential search — always fill residential
  let updatedValues = autoCompleteAddressFields(
    placeResult,
    currentStepData,
    selectedValues,
    'residential'
  );

  return updatedValues;
};

export const getAddressRelatedFields = (
  fields: FormField[],
  addressType: 'residential' | 'postal'
): FormField[] => {
  const prefix = addressType === 'residential' ? 'residentialAddress' : 'postalAddress';
  return fields.filter((field) =>
    field.system_identifire?.startsWith(prefix)
  );
};

export const isAddressComplete = (
  addressComponents: AddressComponents
): boolean => {
  // Require at least street number, street name, suburb, state, postcode
  return !!(
    addressComponents.streetNumber &&
    addressComponents.streetName &&
    addressComponents.suburb &&
    addressComponents.state &&
    addressComponents.postcode
  );
};

export const formatAddressForDisplay = (
  addressComponents: AddressComponents
): string => {
  const parts = [
    addressComponents.streetNumber,
    addressComponents.streetType ? `${addressComponents.streetName} ${addressComponents.streetType}` : addressComponents.streetName,
    addressComponents.unitNumber && `Unit ${addressComponents.unitNumber}`,
    addressComponents.suburb,
    addressComponents.state,
    addressComponents.postcode,
    addressComponents.countryCode,
  ].filter(Boolean);

  return parts.join(', ');
};
